/**
 * PR concept diagram for the pull-requests course intro step.
 *
 * Shows the canonical PR picture: a `main` lane with a `feature` branch
 * diverging, and a labeled "Pull request" arrow proposing the merge back.
 */
export function PRConceptDiagram() {
  const mainY = 150;
  const featY = 70;
  const leftX = 60;
  const stepX = 90;

  // main commits: 0, 1, 2
  const mainXs = [leftX, leftX + stepX, leftX + stepX * 2];
  // feature branches off main[2], two commits
  const featXs = [leftX + stepX * 3, leftX + stepX * 4];
  // tipX = where the PR arrow goes back into main
  const prTargetX = leftX + stepX * 5;

  return (
    <div
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--border-1)",
        borderRadius: 14,
        padding: 22,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          color: "var(--fg-2)",
        }}
      >
        On GitHub (origin)
      </div>

      <div style={{ position: "relative", flex: 1, minHeight: 240 }}>
        <svg
          viewBox="0 0 600 260"
          style={{ width: "100%", height: "100%", display: "block" }}
        >
          <defs>
            <linearGradient id="pr-arrow" x1="0%" x2="100%" y1="0%" y2="0%">
              <stop offset="0%" stopColor="#2D9BD6" />
              <stop offset="100%" stopColor="#7B4FB8" />
            </linearGradient>
            <marker
              id="pr-arrowhead"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#7B4FB8" />
            </marker>
          </defs>

          {/* main lane line */}
          <line
            x1={leftX - 20}
            y1={mainY}
            x2={prTargetX + 40}
            y2={mainY}
            stroke="rgba(255,255,255,0.18)"
            strokeWidth="2"
          />

          {/* branch divergence: main[2] -> feature[0] */}
          <path
            d={`M ${mainXs[2]} ${mainY} C ${(mainXs[2] + featXs[0]) / 2} ${mainY}, ${(mainXs[2] + featXs[0]) / 2} ${featY}, ${featXs[0]} ${featY}`}
            stroke="#2D9BD6"
            strokeWidth="2"
            fill="none"
          />
          {/* feature lane between feature commits */}
          <line
            x1={featXs[0]}
            y1={featY}
            x2={featXs[1]}
            y2={featY}
            stroke="#2D9BD6"
            strokeWidth="2"
          />

          {/* PR arrow: feature tip back into main, dashed + gradient */}
          <path
            d={`M ${featXs[1]} ${featY} C ${(featXs[1] + prTargetX) / 2} ${featY}, ${(featXs[1] + prTargetX) / 2} ${mainY}, ${prTargetX} ${mainY}`}
            stroke="url(#pr-arrow)"
            strokeWidth="3"
            strokeDasharray="6 5"
            fill="none"
            markerEnd="url(#pr-arrowhead)"
          />

          {/* main commits */}
          {mainXs.map((x, i) => (
            <g key={`m-${i}`}>
              <circle cx={x} cy={mainY} r={11} fill="#fff" stroke="#001847" strokeWidth="2" />
              <text
                x={x}
                y={mainY + 4}
                textAnchor="middle"
                fontSize="10"
                fontFamily="ui-monospace, Menlo"
                fill="#001847"
              >
                {String.fromCharCode(97 + i)}
              </text>
            </g>
          ))}

          {/* feature commits */}
          {featXs.map((x, i) => (
            <g key={`f-${i}`}>
              <circle cx={x} cy={featY} r={11} fill="#2D9BD6" stroke="#001847" strokeWidth="2" />
              <text
                x={x}
                y={featY + 4}
                textAnchor="middle"
                fontSize="10"
                fontFamily="ui-monospace, Menlo"
                fill="#fff"
              >
                {i === 0 ? "d" : "e"}
              </text>
            </g>
          ))}

          {/* branch labels */}
          <g>
            <rect
              x={leftX - 35}
              y={mainY + 18}
              width={42}
              height={18}
              rx={4}
              fill="rgba(45,155,214,0.15)"
              stroke="rgba(45,155,214,0.6)"
            />
            <text
              x={leftX - 14}
              y={mainY + 31}
              textAnchor="middle"
              fontSize="11"
              fontWeight="600"
              fill="#2D9BD6"
              fontFamily="ui-monospace, Menlo"
            >
              main
            </text>
          </g>
          <g>
            <rect
              x={featXs[1] + 18}
              y={featY - 9}
              width={56}
              height={18}
              rx={4}
              fill="rgba(123,79,184,0.15)"
              stroke="rgba(123,79,184,0.6)"
            />
            <text
              x={featXs[1] + 46}
              y={featY + 4}
              textAnchor="middle"
              fontSize="11"
              fontWeight="600"
              fill="#B084E8"
              fontFamily="ui-monospace, Menlo"
            >
              feature
            </text>
          </g>

          {/* PR label */}
          <g>
            <rect
              x={(featXs[1] + prTargetX) / 2 - 60}
              y={(featY + mainY) / 2 - 13}
              width={120}
              height={26}
              rx={6}
              fill="rgba(123,79,184,0.18)"
              stroke="#7B4FB8"
            />
            <text
              x={(featXs[1] + prTargetX) / 2}
              y={(featY + mainY) / 2 + 4}
              textAnchor="middle"
              fontSize="11"
              fontWeight="700"
              fill="#fff"
              letterSpacing="0.04em"
            >
              PULL REQUEST
            </text>
          </g>
        </svg>
      </div>

      <div
        style={{
          display: "flex",
          gap: 14,
          fontSize: 12,
          color: "var(--fg-2)",
          paddingTop: 8,
          borderTop: "1px solid var(--border-1)",
          flexWrap: "wrap",
        }}
      >
        <span>
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              background: "#fff",
              border: "1px solid #001847",
              borderRadius: 999,
              marginRight: 6,
              verticalAlign: -1,
            }}
          />
          main
        </span>
        <span>
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              background: "#2D9BD6",
              border: "1px solid #001847",
              borderRadius: 999,
              marginRight: 6,
              verticalAlign: -1,
            }}
          />
          feature branch
        </span>
        <span>
          <span
            style={{
              display: "inline-block",
              width: 16,
              height: 3,
              background: "linear-gradient(90deg, #2D9BD6, #7B4FB8)",
              marginRight: 6,
              verticalAlign: 2,
            }}
          />
          PR proposes the merge
        </span>
      </div>
    </div>
  );
}
